import { useMemo, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Presets per module — kept small on purpose (bulk on free text like names/
// emails is not part of the spec). Per-role visibility is enforced server-side.
const FIELD_PRESETS = {
  users: {
    role: ['owner', 'operations', 'accounts', 'commercials', 'hr', 'dm_creative', 'dm_technical', 'dm_architecture', 'team'],
    status: ['active', 'pending', 'disabled', 'inactive'],
    department: null,          // free-text
    reports_to: null,          // free-text (id) — power users only
    record_state: ['active', 'archived', 'on_hold'],
  },
  work_orders: {
    status: ['uploaded', 'parsed', 'verified', 'active', 'closed', 'cancelled'],
    values_status: ['pending', 'entered', 'confirmed'],
    record_state: ['active', 'archived', 'on_hold'],
  },
  visits: {
    status: ['planned', 'completed', 'cancelled'],
    billing_status: ['not_billable', 'pending_bill', 'billed', 'on_hold', 'waived'],
    record_state: ['active', 'archived', 'on_hold'],
  },
  quotes: {
    status: ['draft', 'shared', 'under_negotiation', 'won', 'lost', 'expired', 'on_hold'],
    record_state: ['active', 'archived', 'on_hold'],
  },
};

export function BulkModifyDialog({ module, moduleLabel, ids, editableFields, needsApproval, onClose, onDone }) {
  const [field, setField] = useState('');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const fields = useMemo(() => (editableFields || []).filter(f => (FIELD_PRESETS[module] || {})[f] !== undefined || (FIELD_PRESETS[module] || {})[f] === null), [editableFields, module]);
  const preset = field ? FIELD_PRESETS[module]?.[field] : null;

  const canSubmit = field && value !== '' && !busy;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/bulk/${module}`, {
        ids, action: 'modify', payload: { field, value },
      });
      if (r.data.committed) {
        toast.success(`${moduleLabel}: ${r.data.summary.applied_count} updated`);
      } else {
        toast.success(`Change request sent to the Owner (${r.data.target_count} row${r.data.target_count > 1 ? 's' : ''})`);
      }
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[480px]" data-testid={`bulk-modify-dialog-${module}`}>
        <DialogHeader>
          <DialogTitle>Modify {ids.length} {moduleLabel}</DialogTitle>
          <DialogDescription>
            Pick one field and a new value. It will be set on every selected row.
            {needsApproval && ' The Owner will review and approve before it is applied.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-[12px] font-semibold uppercase tracking-wider text-[hsl(var(--foreground-muted))]">Field</label>
            <Select value={field} onValueChange={(v) => { setField(v); setValue(''); }}>
              <SelectTrigger data-testid={`bulk-modify-field-${module}`}>
                <SelectValue placeholder="Choose field…" />
              </SelectTrigger>
              <SelectContent>
                {fields.map(f => <SelectItem key={f} value={f}>{f.replace(/_/g, ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[12px] font-semibold uppercase tracking-wider text-[hsl(var(--foreground-muted))]">New value</label>
            {preset ? (
              <Select value={value} onValueChange={setValue}>
                <SelectTrigger data-testid={`bulk-modify-value-${module}`}>
                  <SelectValue placeholder="Choose value…" />
                </SelectTrigger>
                <SelectContent>
                  {preset.map(v => <SelectItem key={v} value={v}>{v.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input value={value} onChange={e => setValue(e.target.value)}
                     placeholder="Type value…"
                     disabled={!field}
                     data-testid={`bulk-modify-value-input-${module}`} />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid={`bulk-modify-submit-${module}`}>
            {busy ? 'Submitting…' : (needsApproval ? 'Request approval' : 'Apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
